import { useEffect, useState } from "react";
import { api } from "../api.js";
import { card, btnSecondary } from "../components/ui.js";

interface TenantSummary {
  id: string;
  portalId: string;
  companyName: string;
  plan: string;
  monthlyLimit: number;
  currentMonthUsage: number;
  csEnvironment: string;
  hasCsClientId: boolean;
  hasHubspotToken: boolean;
  hubspotTokenExpiresAt: string | null;
  stripeCustomerId: string | null;
  userCount: number;
  ownerEmail: string | null;
  lastUserLoginAt: string | null;
  totalSendCount: number;
  suspendedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return "-";
  return new Date(s).toLocaleDateString("ja-JP");
}

function formatDateTime(s: string | null | undefined): string {
  if (!s) return "-";
  return new Date(s).toLocaleString("ja-JP");
}

function planLabel(plan: string): string {
  return {
    free: "フリー",
    basic: "ベーシック",
    pro: "プロ",
    enterprise: "エンタープライズ",
  }[plan] ?? plan;
}

export function AdminTenantsTab() {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.adminListTenants();
        setTenants(r.tenants || []);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ color: "#6b6b68" }}>読み込み中...</div>;
  if (error) return <div style={{ color: "#d32f2f" }}>{error}</div>;

  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
        テナント一覧（{tenants.length}社）
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#faf9f7", borderBottom: "1px solid #e8e7e3" }}>
              <th style={thStyle}>会社名</th>
              <th style={thStyle}>PortalID</th>
              <th style={thStyle}>プラン</th>
              <th style={thStyle}>請求期間/累計</th>
              <th style={thStyle}>Users</th>
              <th style={thStyle}>オーナー</th>
              <th style={thStyle}>CS</th>
              <th style={thStyle}>HS</th>
              <th style={thStyle}>最終ログイン</th>
              <th style={thStyle}>作成日</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.id} style={{ borderBottom: "1px solid #f0efeb" }}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                    {t.suspendedAt && (
                      <span
                        title={t.suspendedReason || "利用停止中"}
                        style={{
                          background: "#FEE2E2", color: "#7C1F1F", fontSize: 10,
                          padding: "2px 6px", borderRadius: 4, fontWeight: 600,
                        }}
                      >停止</span>
                    )}
                    <span style={{ opacity: t.suspendedAt ? 0.5 : 1 }}>{t.companyName}</span>
                  </div>
                </td>
                <td style={tdStyle}>{t.portalId}</td>
                <td style={tdStyle}>{planLabel(t.plan)}</td>
                <td style={tdStyle}>
                  {t.currentMonthUsage} / {t.totalSendCount}
                </td>
                <td style={tdStyle}>{t.userCount}</td>
                <td style={tdStyle}>{t.ownerEmail ?? "-"}</td>
                <td style={tdStyle}>
                  <StatusDot ok={t.hasCsClientId} />
                  <span style={{ fontSize: 11, marginLeft: 4, color: "#6b6b68" }}>
                    {t.csEnvironment === "production" ? "本番" : "sandbox"}
                  </span>
                </td>
                <td style={tdStyle}>
                  <StatusDot ok={t.hasHubspotToken} />
                </td>
                <td style={tdStyle}>{formatDateTime(t.lastUserLoginAt)}</td>
                <td style={tdStyle}>{formatDate(t.createdAt)}</td>
                <td style={tdStyle}>
                  <button
                    onClick={() => setSelectedId(t.id)}
                    style={{ ...btnSecondary, padding: "4px 12px", fontSize: 12 }}
                  >
                    詳細
                  </button>
                </td>
              </tr>
            ))}
            {tenants.length === 0 && (
              <tr>
                <td colSpan={11} style={{ ...tdStyle, textAlign: "center", color: "#6b6b68" }}>
                  テナントがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <TenantDetailModal tenantId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: "#6b6b68",
};

const tdStyle: React.CSSProperties = {
  padding: "12px",
  fontSize: 13,
  verticalAlign: "middle",
};

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: ok ? "#1D9E75" : "#ccc",
      }}
    />
  );
}

function TenantDetailModal({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.adminGetTenant(tenantId);
      setData(r.tenant);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleSuspend = async () => {
    const reason = window.prompt(
      "このテナントを利用停止にします。理由（任意・後で参照用）を入力してください。",
      "",
    );
    if (reason === null) return;
    if (!window.confirm(`本当にこのテナント「${data?.companyName ?? ""}」を利用停止にしますか？\nデータは保持され、いつでも復活できます。`)) return;
    setActing(true);
    try {
      await api.adminSuspendTenant(tenantId, reason || undefined);
      await reload();
    } catch (e: any) {
      window.alert(`失敗: ${e.message}`);
    } finally {
      setActing(false);
    }
  };

  const handleUnsuspend = async () => {
    if (!window.confirm(`このテナント「${data?.companyName ?? ""}」の利用停止を解除しますか？`)) return;
    setActing(true);
    try {
      await api.adminUnsuspendTenant(tenantId);
      await reload();
    } catch (e: any) {
      window.alert(`失敗: ${e.message}`);
    } finally {
      setActing(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 12, padding: 28, width: "100%",
          maxWidth: 900, maxHeight: "85vh", overflow: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>テナント詳細</div>
          <button onClick={onClose} style={{ ...btnSecondary, padding: "6px 14px" }}>閉じる</button>
        </div>
        {loading && <div>読み込み中...</div>}
        {error && <div style={{ color: "#d32f2f" }}>{error}</div>}
        {data && (
          <div>
            {data.suspendedAt && (
              <div style={{
                background: "#FEF1F1", border: "1px solid #F5C6C6", borderRadius: 8,
                padding: "12px 16px", marginBottom: 18, fontSize: 13, color: "#7C1F1F",
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  ⛔ このテナントは利用停止中です
                </div>
                <div>停止日時: {formatDateTime(data.suspendedAt)}</div>
                {data.suspendedReason && <div>理由: {data.suspendedReason}</div>}
              </div>
            )}

            <div style={{ marginBottom: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              {data.suspendedAt ? (
                <button
                  onClick={handleUnsuspend}
                  disabled={acting}
                  style={{
                    background: "#1D9E75", color: "white", border: "none",
                    padding: "8px 18px", borderRadius: 8, fontSize: 13,
                    cursor: acting ? "not-allowed" : "pointer", opacity: acting ? 0.6 : 1,
                  }}
                >利用停止を解除</button>
              ) : (
                <button
                  onClick={handleSuspend}
                  disabled={acting}
                  style={{
                    background: "#D32F2F", color: "white", border: "none",
                    padding: "8px 18px", borderRadius: 8, fontSize: 13,
                    cursor: acting ? "not-allowed" : "pointer", opacity: acting ? 0.6 : 1,
                  }}
                >利用停止にする</button>
              )}
            </div>

            <Section title="基本情報">
              <Row label="会社名" value={data.companyName} />
              <Row label="Portal ID" value={data.portalId} />
              <Row label="プラン" value={planLabel(data.plan)} />
              <Row label="月間上限" value={`${data.monthlyLimit} 件`} />
              <Row label="請求期間の送信数" value={`${data.currentMonthUsage} 件`} />
              <Row label="累計送信数" value={`${data._count?.sendLogs ?? 0} 件`} />
              <Row label="Stripe Customer" value={data.stripeCustomerId || "-"} />
              <Row label="作成日時" value={formatDateTime(data.createdAt)} />
              <Row label="更新日時" value={formatDateTime(data.updatedAt)} />
            </Section>

            <Section title="連携状況">
              <Row
                label="クラウドサイン"
                value={
                  <span>
                    <StatusDot ok={data.hasCsClientId} />{" "}
                    {data.hasCsClientId ? "接続済み" : "未接続"}（{data.csEnvironment === "production" ? "本番" : "sandbox"}）
                  </span>
                }
              />
              <Row
                label="HubSpot"
                value={
                  <span>
                    <StatusDot ok={data.hasHubspotToken} /> {data.hasHubspotToken ? "接続済み" : "未接続"}
                    {data.hubspotTokenExpiresAt && (
                      <span style={{ color: "#6b6b68", marginLeft: 8 }}>
                        トークン期限: {formatDateTime(data.hubspotTokenExpiresAt)}
                      </span>
                    )}
                  </span>
                }
              />
            </Section>

            <Section title={`ユーザー（${data.users?.length ?? 0}）`}>
              <MiniTable
                rows={(data.users || []).map((u: any) => ({
                  email: u.email,
                  name: u.name || "-",
                  role: u.role,
                  verified: u.emailVerified ? "✓" : "-",
                  lastLoginAt: formatDateTime(u.lastLoginAt),
                }))}
                cols={[
                  { key: "email", label: "Email" },
                  { key: "name", label: "名前" },
                  { key: "role", label: "ロール" },
                  { key: "verified", label: "認証" },
                  { key: "lastLoginAt", label: "最終ログイン" },
                ]}
              />
            </Section>

            <Section title={`クラウドサイン ClientID（${data.clientIds?.length ?? 0}）`}>
              <MiniTable
                rows={(data.clientIds || []).map((c: any) => ({
                  label: c.label,
                  environment: c.environment === "production" ? "本番" : "sandbox",
                  isDefault: c.isDefault ? "★" : "",
                  createdAt: formatDate(c.createdAt),
                }))}
                cols={[
                  { key: "label", label: "ラベル" },
                  { key: "environment", label: "環境" },
                  { key: "isDefault", label: "既定" },
                  { key: "createdAt", label: "作成日" },
                ]}
              />
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: "#1a1a1a" }}>{title}</div>
      <div style={{ background: "#faf9f7", borderRadius: 8, padding: "4px 16px" }}>{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", padding: "10px 0", borderBottom: "1px solid #efeee9", fontSize: 13 }}>
      <span style={{ width: 160, color: "#6b6b68", flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, wordBreak: "break-all" }}>{value}</span>
    </div>
  );
}

function MiniTable({
  rows,
  cols,
}: {
  rows: Record<string, any>[];
  cols: { key: string; label: string }[];
}) {
  if (rows.length === 0) {
    return <div style={{ padding: "12px 0", color: "#6b6b68", fontSize: 13 }}>（なし）</div>;
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c.key} style={{ textAlign: "left", padding: "8px 0", fontSize: 12, color: "#6b6b68", fontWeight: 600 }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ borderTop: "1px solid #efeee9" }}>
            {cols.map((c) => (
              <td key={c.key} style={{ padding: "8px 0" }}>
                {r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
